import FormInput from "@components/Form/FormInput";
import FormLabel from "@components/Form/FormLabel";
import FormTextarea from "@components/Form/FormTextarea";
import { StyledButton } from "@components/Styled/Button";
import { StyledH2, StyledP, StyledXSmall } from "@components/Styled/Text";
import { css } from "@emotion/react";
import React from "react";
import tw, { styled } from "twin.macro";
import { Icon } from "../Icon";
import FitFeedback from "./FitFeedback";
import { withReviewsForm } from "./withReviewsForm";

const Wrapper = tw.form`
  flex flex-wrap w-full px-midi text-hint md:px-zero md:text-body
`;
const Header = tw(StyledH2)`
  w-full mb-midi text-center md:text-[44px]
`;
const Column = tw.div`
w-full md:w-1/2 md:px-mini
`;
const Button = tw(StyledButton)`
  w-full md:max-w-33
`;
const Input = styled(FormInput)`
  ${tw`px-zero mb-midi h-auto`}
  ${({ error }) =>
    error
      ? css`
          ${tw`mb-zero`}
          & input {
            ${tw`border-b-2 text-warning border-warning`}
          }
        `
      : null}
`;
const Label = styled(FormLabel)`
  ${css`
    label {
      ${tw`pb-micro text-primary`}
    }
  `}
`;
const Textarea = styled(FormTextarea)`
  ${tw`mb-midi px-zero`}
  ${({ error }) =>
    error
      ? css`
          ${tw`mb-zero`}
          & textarea {
            ${tw`border-b-2 text-warning border-warning`}
          }
        `
      : null}
`;
const TextareaWrapper = tw.div`
  w-full md:px-mini
`;
const ReviewTitle = styled(FormInput)`
  ${tw`w-full px-zero mb-midi md:px-mini`}
`;
const StyledIcon = tw(Icon)`mr-pico cursor-pointer text-primary`;
const Rating = tw.div`
  w-full flex flex-wrap px-zero md:px-mini
`;
const Stars = tw.div`
  w-full flex flex-col justify-start text-orange mb-maxi md:w-1/2 md:mb-zero
`;
const ButtonWrapper = tw.div`
  w-full flex items-center justify-center md:w-1/2
`;
const ErrorMessage = tw(StyledXSmall)`
  mb-mini text-orange mt-pico
`;
const Success = tw.div`
  py-midi flex flex-col items-center justify-center md:py-giga
`;
const Title = tw(StyledH2)`
  mb-mini text-center
`;
const Message = tw(StyledP)`
  mx-auto text-center leading-caption mb-mini
`;

const ReviewsForm = withReviewsForm(
  ({
    className,
    data,
    errors,
    fitLength,
    fitWidth,
    gender,
    handleChange,
    handleFitLengthChange,
    handleFitWidthChange,
    handleOptionChange,
    handleRatingChange,
    handleSubmit,
    loading,
    rating,
    success,
  }) =>
    !success ? (
      <Wrapper className={className}>
        <Header>Leave a Review</Header>
        <Column>
          <Input
            data-testid="name"
            name="name"
            label="Name"
            value={data.name}
            handleChange={handleChange}
            error={!!errors["name"]}
            required
          />
          {errors["name"] ? (
            <ErrorMessage>{errors["name"]}</ErrorMessage>
          ) : null}
        </Column>
        <Column>
          <Input
            data-testid="email"
            name="email"
            label="Email"
            value={data.email}
            handleChange={handleChange}
            error={!!errors["email"]}
            required
          />
          {errors["email"] ? (
            <ErrorMessage>{errors["email"]}</ErrorMessage>
          ) : null}
        </Column>
        {process.env["GATSBY_FEATURE_REVIEWS_ENHANCEMENT"] &&
        (gender === "Mens" || gender === "Womens") ? (
          <FitFeedback
            fitLength={fitLength}
            fitWidth={fitWidth}
            errors={errors}
            gender={gender}
            handleFitLengthChange={handleFitLengthChange}
            handleFitWidthChange={handleFitWidthChange}
            handleSizeChange={handleOptionChange}
            handleTitleChange={handleChange}
            title={data.title}
          />
        ) : (
          <ReviewTitle
            data-testid="review-title"
            name="title"
            label="Review Title"
            value={data.title}
            handleChange={handleChange}
          />
        )}
        <TextareaWrapper>
          <Textarea
            data-testid="review-content"
            name="content"
            label="Leave a review"
            value={data.content}
            handleChange={handleChange}
            error={!!errors["content"]}
            required
          />
          {errors["content"] ? (
            <ErrorMessage>{errors["content"]}</ErrorMessage>
          ) : null}
        </TextareaWrapper>
        <Rating>
          <Stars>
            <Label id="rating" label="Rating" required />
            <div>
              {Array.from({ length: 5 }).map((_val, index) => (
                <StyledIcon
                  key={index}
                  name={index + 1 <= rating ? "filledStar" : "star"}
                  size="none"
                  width="20"
                  height="20"
                  onClick={() => handleRatingChange(index + 1)}
                />
              ))}
            </div>
          </Stars>
          <ButtonWrapper>
            <Button
              onClick={handleSubmit}
              height="56"
              textSize="16"
              colour="primary"
              disabled={loading}
            >
              {loading ? "Submitting" : "Submit"}
            </Button>
          </ButtonWrapper>
        </Rating>
        {errors["unknown"] ? (
          <ErrorMessage>{errors["unknown"]}</ErrorMessage>
        ) : null}
      </Wrapper>
    ) : (
      <Success>
        <Title>Thanks for your review!</Title>
        <Message>
          We love hearing from you. Our team will publish it shortly.
        </Message>
      </Success>
    ),
);

export default ReviewsForm;
